import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Icon from '../Icon.jsx';
import { CATEGORY_ICONS, catIconUrl } from '../catIcons.js';
import PageSkeleton from '../Skeleton.jsx';

/** Panelde tutar "14.50" yazılır, sunucuya 1450 gider. Float ASLA gönderilmez. */
const toMinor = (text) => {
  const n = Number(String(text).replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};
const fromMinor = (m) => (m / 100).toFixed(2);

/**
 * Kategori simgesi seçici.
 *
 * Simge ADI saklanır (sunucu beyaz listeye bakar), görsel `src/assets/cat`
 * altından gelir: restoran resim yüklemez, hazır takımdan seçer — böylece
 * misafir arayüzünde ve basılı menüde hep aynı çizim dili çıkar.
 */
function IconPick({ value, onPick, t }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const label = value ? t(`cat.icon.${value}`) : t('cat.iconNone');
  return (
    <div className="iconpick" ref={box}>
      <button className="iconpick-btn" onClick={() => setOpen(!open)}
        aria-haspopup="menu" aria-expanded={open} title={`${t('cat.icon')}: ${label}`}>
        {value
          ? <img src={catIconUrl(value)} alt="" />
          : <Icon n="plusCircle" />}
      </button>
      {open && (
        <div className="iconpick-pop" role="menu">
          <div className="iconpick-grid">
            {CATEGORY_ICONS.map((k) => (
              <button key={k} className={k === value ? 'on' : ''} title={t(`cat.icon.${k}`)}
                aria-label={t(`cat.icon.${k}`)}
                onClick={() => { onPick(k); setOpen(false); }}>
                <img src={catIconUrl(k)} alt="" />
              </button>
            ))}
          </div>
          <button className="block" onClick={() => { onPick(null); setOpen(false); }}>
            <Icon n="ban" />{t('cat.iconNone')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Menu() {
  const t = useT();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [newCat, setNewCat] = useState('');
  const [newCatIcon, setNewCatIcon] = useState(null);
  const [newItem, setNewItem] = useState({});
  const [openItem, setOpenItem] = useState(null);
  // Kategoriler KAPALI başlar: on kategorili bir menüde ekran yüzlerce satır
  // açıyordu. Kapalı kartta ürün adları tek özet satırına iner — hangi
  // kategoride ne olduğu açmadan görünür.
  const [openCats, setOpenCats] = useState(() => new Set());
  const toggleCat = (id) => setOpenCats((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const load = () => api('/api/menu').then(setData).catch((e) => setErr(errMessage(t, e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const call = async (path, opts) => {
    setErr('');
    try { const r = await api(path, opts); load(); return r; } catch (e) { setErr(errMessage(t, e)); }
  };

  if (err && !data) return <p className="err">{err}</p>;
  if (!data) return <PageSkeleton shape="menu" />;

  return (
    <>
      <div className="card">
        <div className="row">
          <h2 style={{ margin: 0 }}>{t('nav.menu')}</h2>
          <span className="spacer" />
          {/* Basılı menü aynı veriden üretilir — yazdırma merkezi menü biçiminde açılır. */}
          <Link to="/qr?mode=menu"><button><Icon n="printer" />{t('menu.printMenu')}</button></Link>
        </div>
      </div>

      {data.categories.length === 0 && (
        <div className="card">
          <h2>{t('menu.empty')}</h2>
          <p className="muted" style={{ margin: 0 }}>{t('menu.emptyNote', { currency: data.currency })}</p>
        </div>
      )}

      {data.categories.map((cat) => {
      const open = openCats.has(cat.id);
      return (
        <div className="card" key={cat.id}>
          <div className="row">
            <button className="btn-ico" onClick={() => toggleCat(cat.id)}
              aria-expanded={open} aria-controls={`cat-${cat.id}`}
              aria-label={open ? t('collapse') : t('expand')} title={open ? t('collapse') : t('expand')}>
              <Icon n={open ? 'chevronUp' : 'chevronDown'} />
            </button>
            <IconPick value={cat.icon} t={t}
              onPick={(icon) => call(`/api/menu/categories/${cat.id}`, { method: 'PATCH', body: { icon } })} />
            <input style={{ maxWidth: 200, fontWeight: 600 }} defaultValue={cat.name}
              onBlur={(e) => e.target.value !== cat.name && call(`/api/menu/categories/${cat.id}`, { method: 'PATCH', body: { name: e.target.value } })} />
            <label className="switch" style={{ marginBottom: 0 }}>
              <input type="checkbox" checked={cat.visible}
                onChange={(e) => call(`/api/menu/categories/${cat.id}`, { method: 'PATCH', body: { visible: e.target.checked } })} />
              <span className="muted">{t('menu.visibleToGuest')}</span>
            </label>
            <span className="spacer" />
            <span className="muted">{t('menu.itemCount', { n: cat.items.length })}</span>
            <button onClick={() => confirm(t('menu.confirmArchiveCategory', { name: cat.name }))
              && call(`/api/menu/categories/${cat.id}`, { method: 'DELETE' })}>
              <Icon n="archive" />{t('archive')}
            </button>
          </div>

          {/* Kapalı kart: içindekiler alt açıklama olarak. Satırın kendisi de
              açar — kullanıcı oka nişan almak zorunda kalmasın. */}
          {!open && (
            <button className="cat-sum" onClick={() => toggleCat(cat.id)}
              aria-expanded={false} aria-controls={`cat-${cat.id}`}>
              <span>{cat.items.length ? cat.items.map((i) => i.name).join(' · ') : t('menu.noItems')}</span>
            </button>
          )}

          <div id={`cat-${cat.id}`} hidden={!open}>
          <table style={{ marginTop: 12 }}>
            <thead>
              <tr><th>{t('menu.item')}</th><th style={{ width: 120 }}>{t('menu.price', { currency: data.currency })}</th><th style={{ width: 100 }}>{t('menu.inStock')}</th><th style={{ width: 150 }}>{t('menu.options')}</th><th></th></tr>
            </thead>
            <tbody>
              {cat.items.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input defaultValue={it.name}
                      onBlur={(e) => e.target.value !== it.name && call(`/api/menu/items/${it.id}`, { method: 'PATCH', body: { name: e.target.value } })} />
                    {it.description && <div className="muted">{it.description}</div>}
                  </td>
                  <td>
                    <input defaultValue={fromMinor(it.priceMinor)}
                      onBlur={(e) => {
                        if (e.target.value === fromMinor(it.priceMinor)) return;
                        const minor = toMinor(e.target.value);
                        if (minor == null || minor < 0) return setErr(t('menu.invalidPrice'));
                        call(`/api/menu/items/${it.id}`, { method: 'PATCH', body: { priceMinor: minor } });
                      }} />
                  </td>
                  <td>
                    <div className="switch">
                      <input type="checkbox" checked={it.available}
                        onChange={(e) => call(`/api/menu/items/${it.id}/availability`, { method: 'PATCH', body: { available: e.target.checked } })} />
                    </div>
                  </td>
                  <td>
                    <button onClick={() => setOpenItem(openItem === it.id ? null : it.id)}>
                      <Icon n="layers" />{t('menu.groupCount', { n: it.optionGroups.length })}
                      <Icon n={openItem === it.id ? 'chevronUp' : 'chevronDown'} />
                    </button>
                  </td>
                  <td>
                    <button onClick={() => confirm(t('menu.confirmArchiveItem', { name: it.name })) && call(`/api/menu/items/${it.id}`, { method: 'DELETE' })}>
                      <Icon n="archive" />{t('archive')}
                    </button>
                  </td>
                </tr>
              ))}
              {openItem && cat.items.some((i) => i.id === openItem) && (
                <tr>
                  <td colSpan={5} style={{ background: 'var(--panel-2)' }}>
                    <OptionGroups item={cat.items.find((i) => i.id === openItem)} currency={data.currency} call={call} setErr={setErr} t={t} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="row" style={{ marginTop: 12 }}>
            <input style={{ maxWidth: 220 }} placeholder={t('menu.newItemName')}
              value={newItem[cat.id]?.name || ''}
              onChange={(e) => setNewItem({ ...newItem, [cat.id]: { ...newItem[cat.id], name: e.target.value } })} />
            <input style={{ maxWidth: 120 }} placeholder="14.50"
              value={newItem[cat.id]?.price || ''}
              onChange={(e) => setNewItem({ ...newItem, [cat.id]: { ...newItem[cat.id], price: e.target.value } })} />
            <button className="primary" onClick={() => {
              const f = newItem[cat.id] || {};
              const minor = toMinor(f.price);
              if (!f.name || minor == null || minor < 0) return setErr(t('menu.nameAndPriceRequired'));
              call('/api/menu/items', { method: 'POST', body: { categoryId: cat.id, name: f.name, priceMinor: minor } });
              setNewItem({ ...newItem, [cat.id]: {} });
            }}><Icon n="plus" />{t('menu.addItem')}</button>
          </div>
          </div>
        </div>
      );
      })}

      <div className="card">
        <h2>{t('menu.newCategory')}</h2>
        <div className="row">
          <IconPick value={newCatIcon} onPick={setNewCatIcon} t={t} />
          <input style={{ maxWidth: 240 }} placeholder="Pizza" value={newCat} onChange={(e) => setNewCat(e.target.value)} />
          <button className="primary" onClick={async () => {
            if (!newCat.trim()) return;
            const c = await call('/api/menu/categories', { method: 'POST', body: { name: newCat, icon: newCatIcon } });
            // Yeni kategori AÇIK gelir: sıradaki iş zaten ürün girmek.
            if (c?.id) setOpenCats((prev) => new Set(prev).add(c.id));
            setNewCat(''); setNewCatIcon(null);
          }}><Icon n="plus" />{t('add')}</button>
        </div>
      </div>

      {err && <p className="err">{err}</p>}
    </>
  );
}

function OptionGroups({ item, currency, call, setErr, t }) {
  const [gName, setGName] = useState('');
  const [opt, setOpt] = useState({});
  return (
    <div style={{ padding: '8px 0' }}>
      {item.optionGroups.map((g) => (
        <div key={g.id} style={{ marginBottom: 10 }}>
          <div className="row">
            <b>{g.name}</b>
            <span className="muted">
              {t('menu.selectRange', { min: g.minSelect, max: g.maxSelect })} {g.required ? t('menu.required') : ''}
            </span>
            <span className="spacer" />
            <button onClick={() => call(`/api/menu/option-groups/${g.id}`, { method: 'DELETE' })}>
              <Icon n="trash" />{t('menu.deleteGroup')}
            </button>
          </div>
          <div className="row" style={{ marginTop: 6 }}>
            {g.options.map((o) => (
              <span className="badge" key={o.id}>
                {o.name} {o.priceDeltaMinor ? `${o.priceDeltaMinor > 0 ? '+' : ''}${(o.priceDeltaMinor / 100).toFixed(2)}` : ''}
                <button className="btn-ico" style={{ marginLeft: 6 }} aria-label={t('remove')} title={t('remove')}
                  onClick={() => call(`/api/menu/options/${o.id}`, { method: 'DELETE' })}><Icon n="trash" /></button>
              </span>
            ))}
          </div>
          <div className="row" style={{ marginTop: 6 }}>
            <input style={{ maxWidth: 160 }} placeholder={t('menu.optionName')}
              value={opt[g.id]?.name || ''} onChange={(e) => setOpt({ ...opt, [g.id]: { ...opt[g.id], name: e.target.value } })} />
            <input style={{ maxWidth: 110 }} placeholder={t('menu.optionDelta', { currency })}
              value={opt[g.id]?.delta || ''} onChange={(e) => setOpt({ ...opt, [g.id]: { ...opt[g.id], delta: e.target.value } })} />
            <button onClick={() => {
              const f = opt[g.id] || {};
              const n = f.delta ? Number(String(f.delta).replace(',', '.')) : 0;
              if (!f.name || !Number.isFinite(n)) return setErr(t('menu.optionRequired'));
              call(`/api/menu/option-groups/${g.id}/options`, { method: 'POST', body: { name: f.name, priceDeltaMinor: Math.round(n * 100) } });
              setOpt({ ...opt, [g.id]: {} });
            }}><Icon n="plus" />{t('add')}</button>
          </div>
        </div>
      ))}
      <div className="row">
        <input style={{ maxWidth: 200 }} placeholder={t('menu.newGroup')} value={gName} onChange={(e) => setGName(e.target.value)} />
        <button onClick={() => {
          if (!gName.trim()) return;
          call(`/api/menu/items/${item.id}/option-groups`, { method: 'POST', body: { name: gName, minSelect: 0, maxSelect: 5 } });
          setGName('');
        }}><Icon n="plus" />{t('menu.addGroup')}</button>
      </div>
    </div>
  );
}
